import frame8Svg from '@listening/assets/astro/images/Frame-8.svg';
import frame9Svg from '@listening/assets/astro/images/Frame-9.svg';
import frame10Svg from '@listening/assets/astro/images/Frame-10.svg';
import frame11Svg_ from '@listening/assets/astro/images/Frame-11.svg';
import frame12Svg from '@listening/assets/astro/images/Frame-12.svg';
import frame93Png from '@listening/assets/astro/images/Frame-93.png';
import frame113Png from '@listening/assets/astro/images/Frame-113.png';
import frame114Png from '@listening/assets/astro/images/Frame-114.png';
import frame1362789895Png from '@listening/assets/astro/images/Frame-1362789895.png';
import groupPng from '@listening/assets/astro/images/Group.png';
import wepikExport20231229152436kG441Png from '@listening/assets/astro/images/wepik-export-20231229152436kG44-1.png';

import { CtaButton } from '@/components/astro/cta-button/cta-button';

import { useMarketingPricingArm } from '@/hooks/use-marketing-pricing-arm';
import {
  marketingPricingForArm,
  useTrialDays,
} from '@/server/services/pricing-service';

const fmtTrialDays = (daysNum: number) => {
  if (daysNum === 1) return '1 day';
  if (daysNum === 7) return '1 week';
  if (daysNum === 28) return '4 week';
  return `${daysNum.toString()} days`;
};

/**
 * Rendered on `/audio`, `/youtube`, `/college`, `/edu`, `/newsletter` and
 * `/ref`. Only `/audio` is registered for the price test (more edge-registry
 * entries do not fit the CloudFront byte budget — the measurement is in
 * `experimental-routes.ts`), so elsewhere `useMarketingPricingArm` answers
 * `null` and these figures are the fallback arm's — the top live arm, $59/yr
 * and $4.92/mo. The hook is
 * read rather than the fallback hard-coded so the numbers follow the visitor
 * the day one of these routes IS registered.
 */
export function FeaturesCardSection() {
  const trialDays = useTrialDays();
  const pricing = marketingPricingForArm(useMarketingPricingArm());
  return (
    <section className='px-5 sm:px-0'>
      <div className='static inset-x-0 top-auto bottom-[-27px] flex flex-col items-center justify-center gap-8 px-9 pt-18 sm:bottom-0 sm:gap-14 sm:px-14 md:px-0 lg:bottom-auto lg:items-center lg:gap-18 lg:px-18 lg:pt-0'>
        <div className='flex flex-col gap-4'>
          <div
            className='flex flex-col items-center gap-6 pb-[24px]'
            id='features'
          >
            <div className='flex items-center justify-center gap-3 rounded-full bg-azure-455/10 px-4 py-[5px]'>
              <div className='rotate-90 text-[20px] leading-[27px] font-[500]'>
                👆
              </div>
              <div className='text-[12px] leading-[16px] font-[600] text-neutral-945 sm:text-[16px] sm:leading-[21.6px]'>
                GIVE IT A TRY
              </div>
            </div>
            <h2 className='text-center text-[32px] leading-[34px] font-[700] text-neutral-945 sm:text-[40px] sm:leading-[43px] lg:text-[48px] lg:leading-[48px]'>
              Try listening <span className='text-azure-595'>free for</span>{' '}
              {fmtTrialDays(trialDays)}
            </h2>
          </div>
          <div className='text-center text-[16px] leading-[21px] text-neutral-840 sm:text-[20px] sm:leading-[27px]'>
            <div>
              Try unlimited listening. After {fmtTrialDays(trialDays)}, it's
              only {`$${pricing.monthlyUsd.toString()}`}/mo or{' '}
              {`$${pricing.yearlyUsd.toString()}`}/yr. <br /> Cancel anytime
              with 1 click.
            </div>
          </div>
        </div>
        <div className='relative inset-x-0 top-auto bottom-0 flex w-full max-w-[1296px] flex-col-reverse items-center gap-0 overflow-hidden rounded-[56px] bg-white lg:flex-row'>
          <div className='relative z-0 h-[400px] w-[430px] flex-[1_0_auto]'>
            <img
              alt='Features illustration'
              className='absolute inset-x-0 top-0 bottom-[100px] max-w-none [transform:rotateX(0)rotateY(180deg)rotateZ(0)] object-fill [transform-style:preserve-3d]'
              loading='eager'
              src={wepikExport20231229152436kG441Png}
              width='398'
            />
            <img
              alt='Decorative frame'
              className='absolute bottom-[301px] left-0'
              loading='lazy'
              src={frame93Png}
            />
            <img
              alt='Decorative element'
              className='absolute bottom-[231px] left-[-196px]'
              loading='lazy'
              src={frame1362789895Png}
            />
            <img
              alt='Decorative icon'
              className='absolute bottom-[276px] left-[344px] w-[37px]'
              height='37'
              loading='lazy'
              src={frame113Png}
              width='37'
            />
            <div className='absolute bottom-[201px] left-[365px] text-[20px] leading-[27px] font-[600]'>
              🤌
            </div>
            <img
              alt='Decorative icon'
              className='absolute bottom-[340px] left-[301px] w-[37px]'
              height='37'
              loading='lazy'
              src={frame114Png}
              width='37'
            />
          </div>
          <div className='flex flex-[2_auto] flex-col items-start gap-8 self-stretch px-5 py-8 sm:gap-6 sm:px-12 sm:py-18 lg:self-auto lg:px-[14px] lg:py-12'>
            <div className='flex items-start gap-4'>
              <img alt='Icon' loading='lazy' src={frame8Svg} />
              <div className='text-[16px] leading-[21px] font-[400] text-neutral-910 sm:text-[24px] sm:leading-[32px] lg:text-[20px] lg:leading-[27px]'>
                Listen to <span className='font-[500]'>unlimited</span> research
                papers
              </div>
            </div>
            <div className='flex items-start gap-4'>
              <img alt='Icon' loading='lazy' src={frame9Svg} />
              <div className='text-[16px] leading-[21px] font-[400] text-neutral-910 sm:text-[24px] sm:leading-[32px] lg:text-[20px] lg:leading-[27px]'>
                <span className='font-[500]'>Take notes</span> while you listen
                (with 1 click)
              </div>
            </div>
            <div className='flex items-start gap-4'>
              <img alt='Icon' loading='lazy' src={frame10Svg} />
              <div className='text-[16px] leading-[21px] font-[400] text-neutral-910 sm:text-[24px] sm:leading-[32px] lg:text-[20px] lg:leading-[27px]'>
                Upload from{' '}
                <span className='font-[500]'>mobile or desktop</span>
              </div>
            </div>
            <div className='flex items-start gap-4'>
              <img alt='Icon' loading='lazy' src={frame11Svg_} />
              <div className='text-[16px] leading-[21px] font-[400] text-neutral-910 sm:text-[24px] sm:leading-[32px] lg:text-[20px] lg:leading-[27px]'>
                <span className='font-[500]'>Pick which sections</span> to
                listen to
              </div>
            </div>
            <div className='flex items-start gap-4'>
              <img alt='Icon' loading='lazy' src={frame12Svg} />
              <div className='text-[16px] leading-[21px] font-[400] text-neutral-910 sm:text-[24px] sm:leading-[32px] lg:text-[20px] lg:leading-[27px]'>
                <span className='font-[500]'>Bonus:</span> Listen to web pages &
                emails
              </div>
            </div>
          </div>
          <img
            alt='Background pattern'
            className='absolute inset-0 min-h-full max-w-none overflow-hidden object-cover opacity-[.15]'
            loading='lazy'
            src={groupPng}
          />
        </div>
        <CtaButton className='inline-block h-auto cursor-pointer rounded-full border-0 bg-azure-455 p-4 text-center text-[18px] leading-[24px] font-[500] text-neutral-50 no-underline sm:px-6 sm:text-[28px] sm:leading-[37px] lg:px-16 lg:py-5' />
      </div>
    </section>
  );
}
